import { Trophy, Crown, Medal, Award, LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/contexts/ThemeContext';
import { useAvatar } from '@/hooks/useAvatar';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api/client';

interface Buyer {
  playerId: string;
  playerNick: string;
  totalSpent: number;
  orderCount: number;
}

interface TopBuyersWidgetProps {
  storeId: string;
}

const rankIcons: Record<number, LucideIcon> = {
  1: Crown,
  2: Medal,
  3: Award,
};

const rankColors: Record<number, string> = {
  1: 'text-yellow-400',
  2: 'text-gray-400',
  3: 'text-amber-600',
};

const rankBgColors: Record<number, string> = {
  1: 'bg-yellow-400/10 border-yellow-400/30',
  2: 'bg-gray-400/10 border-gray-400/30',
  3: 'bg-amber-600/10 border-amber-600/30',
};

export function TopBuyersWidget({ storeId }: TopBuyersWidgetProps) {
  const { config } = useTheme();
  const { getAvatarUrl } = useAvatar();
  const { t } = useTranslation();
  const [topBuyers, setTopBuyers] = useState<Buyer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopBuyers = async () => {
      try {
        const data = await apiClient.get<{ topBuyers: Buyer[] }>(
          `/public/stores/${storeId}/top-buyers?limit=5`,
        );
        setTopBuyers(data.topBuyers || []);
      } catch {
        // silent — widget simply shows empty state
      } finally {
        setLoading(false);
      }
    };

    fetchTopBuyers();
  }, [storeId]);

  const formatPrice = (price: number) => {
    const priceInReais = price / 100;
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(priceInReais);
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-icon" />
            {t('store.widgets.top_buyers.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            {t('store.widgets.top_buyers.loading')}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="w-5 h-5 text-icon" />
          {t('store.widgets.top_buyers.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {topBuyers.slice(0, 5).map((buyer, index) => {
          const rank = index + 1;
          const RankIcon = rankIcons[rank] || Trophy;
          const rankColor = rankColors[rank] || 'text-muted-foreground';
          const rankBg = rankBgColors[rank] || 'bg-muted border-border';

          return (
            <div
              key={buyer.playerId}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                rank <= 3 ? rankBg : 'bg-background/50 border-border'
              }`}
            >
              {/* Rank */}
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                rank <= 3 ? '' : 'bg-muted'
              }`}>
                {rank <= 3 ? (
                  <RankIcon className={`w-5 h-5 ${rankColor}`} />
                ) : (
                  <span className="text-sm font-bold text-muted-foreground">
                    {rank}
                  </span>
                )}
              </div>

              {/* Avatar */}
              <Avatar className="h-10 w-10">
                <AvatarImage src={getAvatarUrl(buyer.playerNick, 128)} />
                <AvatarFallback className="bg-primary/20 text-primary text-sm">
                  {buyer.playerNick.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-foreground">
                  {buyer.playerNick}
                </p>
                <p className="text-xs text-muted-foreground">
                  {buyer.orderCount}{' '}
                  {t(
                    buyer.orderCount === 1
                      ? 'store.widgets.top_buyers.purchase_one'
                      : 'store.widgets.top_buyers.purchase_other',
                  )}
                </p>
              </div>

              {/* Amount */}
              <Badge
                variant="secondary"
                className={`font-display font-bold ${
                  rank === 1 ? 'bg-yellow-400/20 text-yellow-400' : ''
                }`}
              >
                {formatPrice(buyer.totalSpent)}
              </Badge>
            </div>
          );
        })}

        {topBuyers.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            {t('store.widgets.top_buyers.empty')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default TopBuyersWidget;
